import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { FaArrowLeft, FaMoneyBillWave } from 'react-icons/fa';

const RefundPayment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [isQueueUsed, setIsQueueUsed] = useState(false);

  useEffect(() => {
    fetchBill();
    checkQueue();
  }, [id]);

  const fetchBill = async () => {
    try {
      const res = await axios.get(`/api/billings.php?id=${id}`);
      setBill(res.data);
      setRefundAmount(res.data.paidAmount);
    } catch (err) {
      setError('Failed to load bill data');
    } finally {
      setLoading(false);
    }
  };

  const checkQueue = async () => {
    try {
      // Get all queue records and check if this bill is used
      const res = await axios.get('/api/queue.php', { params: { allRecords: true } });
      const relatedQueue = res.data.find(q => q.billingId === parseInt(id));
      
      if (relatedQueue && relatedQueue.status === 'completed') {
        setIsQueueUsed(true);
      } else {
        setIsQueueUsed(false);
      }
    } catch (err) {
      console.error('Error checking queue:', err);
      // If error, assume not used to allow refund
      setIsQueueUsed(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (parseFloat(refundAmount) > parseFloat(bill.paidAmount)) {
      setError('Cancellation amount cannot exceed paid amount');
      return;
    }

    setProcessing(true);
    try {
      const newPaidAmount = parseFloat(bill.paidAmount) - parseFloat(refundAmount);
      
      // Calculate new payment status
      let newPaymentStatus = 'pending';
      if (newPaidAmount >= parseFloat(bill.totalAmount)) {
        newPaymentStatus = 'complete';
      } else if (newPaidAmount > 0) {
        newPaymentStatus = 'partial';
      }
      
      await axios.put(`/api/billings.php?id=${id}`, {
        patientId: bill.patientId,
        doctorId: bill.doctorId,
        totalAmount: bill.totalAmount,
        paidAmount: newPaidAmount,
        paymentStatus: newPaymentStatus,
        paymentMethod: bill.paymentMethod || 'cash',
        billItems: bill.billItems || [],
        discount: bill.discount || 0,
        tax: bill.tax || 0,
        notes: `${bill.notes || ''}\n[REFUND: ${refundAmount} PKR - Reason: ${refundReason}]`
      });
      
      // Find and cancel queue entry for this bill
      try {
        const queueRes = await axios.get('/api/queue.php', { params: { allRecords: true } });
        const relatedQueue = queueRes.data.find(q => q.billingId === parseInt(id));
        if (relatedQueue && relatedQueue.status !== 'completed') {
          // Use correct API endpoint format
          await axios.put('/api/queue.php', { 
            id: relatedQueue.id,
            status: 'cancelled',
            notes: `Cancelled due to bill refund: ${refundReason}`
          });
        }
      } catch (queueErr) {
        console.error('Error cancelling queue:', queueErr);
      }

      navigate(`/billing/${id}`, {
        state: { notification: { type: 'success', message: 'Bill cancelled and refund processed successfully' } }
      });
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to process cancellation');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading...</p>
      </div>
    );
  }

  if (!bill) {
    return <Alert variant="danger">Bill not found</Alert>;
  }

  if (bill.paidAmount <= 0) {
    return <Alert variant="warning">No payment to cancel</Alert>;
  }

  if (isQueueUsed) {
    return (
      <Alert variant="danger">
        Cannot cancel this bill. Patient has already been examined by the doctor.
      </Alert>
    );
  }

  return (
    <div className="my-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Cancel Bill & Process Refund</h2>
        <Button variant="outline-secondary" onClick={() => navigate(`/billing/${id}`)}>
          <FaArrowLeft className="me-2" /> Back
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Body>
          <Row className="mb-4">
            <Col md={6}>
              <h5>Bill Information</h5>
              <p><strong>Bill #:</strong> {bill.billNumber}</p>
              <p><strong>Patient:</strong> {bill.Patient?.name}</p>
              <p><strong>Total Amount:</strong> {parseFloat(bill.totalAmount).toFixed(2)} PKR</p>
              <p><strong>Paid Amount:</strong> {parseFloat(bill.paidAmount).toFixed(2)} PKR</p>
            </Col>
          </Row>

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Cancellation Amount *</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                max={bill.paidAmount}
                required
              />
              <Form.Text>Maximum cancellable: {parseFloat(bill.paidAmount).toFixed(2)} PKR</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Cancellation Reason *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                required
              />
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={() => navigate(`/billing/${id}`)}>
                Back
              </Button>
              <Button variant="danger" type="submit" disabled={processing}>
                <FaMoneyBillWave className="me-2" />
                {processing ? 'Processing...' : 'Cancel & Refund'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default RefundPayment;
